import React, { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Shield, Zap, Globe } from 'lucide-react';
import { stats } from '../data/mock';

const Hero = () => {
  const heroRef = useRef(null);
  const isInView = useInView(heroRef, { once: true });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 20;
      
      heroRef.current.style.setProperty('--mouse-x', `${x}px`);
      heroRef.current.style.setProperty('--mouse-y', `${y}px`);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToServices = () => {
    document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 15 }
    }
  };

  const statVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: { delay: 0.5 + i * 0.1, type: 'spring', stiffness: 200 }
    })
  };

  return (
    <section id="home" className="hero" ref={heroRef}>
      {/* Animated Background */}
      <div className="hero-bg">
        <div className="hero-gradient-orb orb-1"></div>
        <div className="hero-gradient-orb orb-2"></div>
        <div className="hero-gradient-orb orb-3"></div>
        <div className="hero-grid"></div>
      </div>

      <motion.div 
        className="hero-content"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <div className="hero-text">
          {/* Badge */}
          <motion.div 
            className="hero-badge"
            variants={itemVariants}
            style={{ opacity: 1 }}
            whileHover={{ scale: 1.05 }}
            data-testid="hero-badge"
          >
            <Shield size={16} />
            <span>ISO 9001:2008 Certified</span>
          </motion.div>

          {/* Main Heading with Letter Animation */}
          <motion.h1 className="hero-title" variants={itemVariants}>
            <motion.span 
              className="title-line"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, type: 'spring', stiffness: 100 }}
            >
              Empowering
            </motion.span>
            <motion.span 
              className="title-line gradient-text"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 100 }}
            >
              Digital Excellence
            </motion.span>
            <motion.span 
              className="title-line"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7, type: 'spring', stiffness: 100 }}
            >
              Since 2014
            </motion.span>
          </motion.h1>

          {/* Tagline with Typewriter Effect */}
          <motion.p 
            className="hero-tagline"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            transition={{ delay: 0.9, duration: 0.5 }}
            style={{ 
              overflow: 'hidden', 
              whiteSpace: 'nowrap',
              borderRight: 'none'
            }}
          >
            A Complete IT Solution
          </motion.p>

          {/* Description */}
          <motion.p 
            className="hero-description"
            variants={itemVariants}
            style={{ opacity: 1 }}
          >
            Transforming businesses with cutting-edge IT infrastructure, 
            networking solutions, and comprehensive hardware services 
            across India.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            className="hero-cta-group"
            variants={itemVariants}
            style={{ opacity: 1 }}
          >
            <motion.button 
              className="btn-primary" 
              onClick={scrollToContact}
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.98 }}
              data-testid="get-started-btn"
            >
              <span>Get Started</span>
              <ArrowRight size={20} />
            </motion.button>
            <motion.button 
              className="btn-secondary" 
              onClick={scrollToServices}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              data-testid="services-btn"
            >
              <span>Our Services</span>
            </motion.button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div 
            className="hero-trust"
            variants={itemVariants}
            style={{ opacity: 1 }}
          >
            <motion.div 
              className="trust-item"
              whileHover={{ scale: 1.1 }}
            >
              <Zap size={20} />
              <span>24/7 Support</span>
            </motion.div>
            <motion.div 
              className="trust-item"
              whileHover={{ scale: 1.1 }}
            >
              <Globe size={20} />
              <span>PAN India</span>
            </motion.div>
          </motion.div>
        </div>

        {/* Stats Section */}
        <div className="hero-stats">
          {stats.map((stat, index) => (
            <motion.div 
              key={index} 
              className="stat-card"
              custom={index}
              variants={statVariants}
              initial="hidden"
              animate="visible"
              whileHover={{ 
                y: -8, 
                boxShadow: '0 20px 50px rgba(59, 130, 246, 0.2)',
                borderColor: '#3b82f6'
              }}
              style={{ opacity: 1 }}
              data-testid={`stat-card-${index}`}
            >
              <motion.span 
                className="stat-value"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.6 + index * 0.1, type: 'spring', stiffness: 200 }}
              >
                {stat.value}
              </motion.span>
              <span className="stat-label">{stat.label}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div 
        className="scroll-indicator"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
        style={{ opacity: 1 }}
      >
        <motion.div 
          className="scroll-mouse"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="scroll-wheel"></div>
        </motion.div>
        <span>Scroll to explore</span>
      </motion.div>
    </section>
  );
};

export default Hero;
